import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import { useNavigate } from '@lib/react-router-dom'

import { AccountTypeModal } from '@pages/Traders-Room/Trading-Accounts/NewTradingAccount/modals/AccountType.modal'
import { RegularCampaignAccountTypeModal } from '@pages/Traders-Room/Trading-Accounts/NewTradingAccount/modals/RegularCampaignAccountType.modal'
import { TradingPlatformModal } from '@pages/Traders-Room/Trading-Accounts/NewTradingAccount/modals/TradingPlatform.modal'
import { DynamicLeverageModal } from '@pages/Traders-Room/Trading-Accounts/modals/DynamicLeverage.modal'
import { LeverageModal } from '@pages/Traders-Room/Trading-Accounts/modals/Leverage.modal'

import { useModal } from '@context/ModalsContext'
import { useSessionLanguage } from '@context/SessionSettingsContext'
import { useEntityConfig } from '@context/TradingAccountEntityConfigurationContext'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { isOne } from '@utils/validations'

import { LeverageTypeDto, isLeverageTypeDynamic } from '@model/LeverageTypeDto'
import { NameDto } from '@model/NameDto'
import {
  PlatformTypeEnum,
  isTMTPlatformType,
  isTradingViewPlatformType,
} from '@model/PlatformTypeEnum'

import { ActionModal } from '@shared/modals/Action.modal'
import { InformationModal } from '@shared/modals/Information.modal'
import { RadioModal } from '@shared/modals/Radio.modal'

import { CreateTradingAccountCampaignFormProps } from './CreateTradingAccountCampaign.types'
import {
  CampaignExtendedNameDto,
  PlatformDataType,
  TradingAccountCreationOptionsFetchType,
} from './CreateTradingAccountCampaignForm'

type ModalsProps = CreateTradingAccountCampaignFormProps

export const useModals = (
  props: ModalsProps,
  platformDataProps: PlatformDataType,
  tradingAccountCreationOptionsFetchProps: TradingAccountCreationOptionsFetchType,
  accountTypes: NameDto<number>[]
) => {
  const { values, hasCampaignQuery, handleSubmit, currencies } = props
  const { setFieldValue, setTouched, setSelectedPlatform } = props
  const { platformsAllowToOpenAccount } = platformDataProps
  const { campaignCurrencies } = tradingAccountCreationOptionsFetchProps
  const navigate = useNavigate()
  const { t } = useTranslation()
  const location = useLocation()
  const apiClient = useApiClient(ClientApiClient)
  const locale = useSessionLanguage()
  const entityConfig = useEntityConfig()
  const getDefaultName = (name: string): string =>
    name.startsWith('Classic') ? `Classic (${t('Most Popular')})` : name
  const updatedAccountTypeName = useMemo(
    () => accountTypes.map((at) => ({ ...at, name: getDefaultName(at.name) })),
    [accountTypes, locale, entityConfig, t]
  )
  const handleSetAccountType = (accountType: NameDto) => {
    setFieldValue('accountType', accountType)
    if (!isLeverageTypeDynamic(values.leverageType?.id) && !isOne(values.leverageTypes.length)) {
      setFieldValue('leverageType', undefined)
    }
  }

  const handleCurrency = (currencyId: string) => {
    setFieldValue('currencyId', currencyId)
    setFieldValue('accountType', undefined)
    if (!isLeverageTypeDynamic(values.leverageType?.id)) {
      setFieldValue('leverageType', undefined)
    }
  }

  const currencyOptions = useMemo(
    () => (values.campaignId ? campaignCurrencies : currencies.map((c) => c.id)),
    [currencies, campaignCurrencies, values.campaignId]
  )

  const handlePlatformSelect = (platform: NameDto) => {
    setFieldValue('wallet', undefined)
    setFieldValue('currencyId', undefined)
    setFieldValue('platformType', platform)
    setSelectedPlatform(platform)
    refreshLeverageType(platform?.id)

    if (platform) {
      props.onChangePlatform(
        platform.id,
        values.introducingBroker?.id ?? props.account?.introducingBroker?.id
      )
    }
  }

  const handleSetLeverage = (selectedLeverage: LeverageTypeDto) => {
    setFieldValue('leverageType', selectedLeverage)
  }

  const handleSetAccountOrCampaign = (value: CampaignExtendedNameDto) => {
    handleCurrencyId()
    refreshLeverageType(undefined)
    location.state = {}
    setSelectedPlatform(undefined)
    if (value.id === '0') {
      setFieldValue('tradingAccount', value)
      setFieldValue('campaignId', null)
      setFieldValue('platformType', undefined)
      setFieldValue('wallet', undefined)
      setFieldValue('accountType', undefined)
      return
    }
    setTouched({ currencyId: false })
    setFieldValue('terms', false)
    setFieldValue('calculationType', value.calculationType)
    setFieldValue('platformType', undefined)
    setFieldValue('accountType', undefined)
    setFieldValue('campaignId', value.id)
    setFieldValue('tradingAccount', value)
  }

  const handleCurrencyId = () =>
    setFieldValue(
      'currencyId',
      isOne(campaignCurrencies.length) ? campaignCurrencies[0] : undefined
    )

  const refreshLeverageType = async (platformId: PlatformTypeEnum | undefined) => {
    let fetchedLeverages: LeverageTypeDto[] = []
    if (platformId) {
      try {
        fetchedLeverages = await apiClient.getLeverageTypes(platformId)
      } catch (error) {
        console.error('Failed to refresh leverages:', error)
      }
    }
    let defaultLeverage: LeverageTypeDto | undefined = undefined
    if (platformId && (isTMTPlatformType(platformId) || isTradingViewPlatformType(platformId))) {
      defaultLeverage = fetchedLeverages.find((leverage) => isLeverageTypeDynamic(leverage.id))
    }
    if (!defaultLeverage && isOne(fetchedLeverages.length)) {
      defaultLeverage = fetchedLeverages[0]
    }
    setFieldValue('leverageType', defaultLeverage)
    setFieldValue('leverageTypes', fetchedLeverages)
  }

  const cancelModal = useModal(ActionModal, {
    onConfirm: () => navigate('/dashboard/traders-room/trading-accounts', { replace: true }),
  })

  const accountTypeModal = useModal(AccountTypeModal, {
    initialValue: values?.accountType?.id?.toString(),
    accountTypes: updatedAccountTypeName,
    isDemo: false,
    onSelect: handleSetAccountType,
  })

  const currencyModal = useModal(RadioModal, {
    title: t('Campaigns.Currency'),
    onSelect: handleCurrency,
    initialValue: values?.currencyId,
    simpleOptions: currencyOptions,
  })

  const dynamicLeverageModal = useModal(DynamicLeverageModal)
  const markupModal = useModal(InformationModal, {
    title: t('Trading Account.Introducing Broker (IB)'),
    description: t('Trading Account.Markup Modal'),
    onClose: handleSubmit,
    primaryLabel: t('Got It'),
    secondaryLabel: false,
  })
  const tradingPlatformModal = useModal(TradingPlatformModal, {
    tradingAccountsAccountInfo: platformsAllowToOpenAccount,
    demo: false,
    initialValue: values.platformType?.id.toString(),
    onConfirm: handlePlatformSelect,
  })
  const regularCampaignAccountTypeModal = useModal(RegularCampaignAccountTypeModal, {
    campaigns: props.data,
    isCampaign: hasCampaignQuery,
    initialValue: values.tradingAccount?.id,
    onSelect: handleSetAccountOrCampaign,
  })
  const leverageModal = useModal(LeverageModal, {
    platformTypeId: values.platformType?.id,
    currentLeverageId: values.leverageType?.id,
    restrictions: [],
    onSelect: handleSetLeverage,
  })

  return {
    values,
    currencyOptions,
    platformsAllowToOpenAccount,
    hasCampaignQuery,
    cancelModal,
    accountTypeModal,
    currencyModal,
    dynamicLeverageModal,
    markupModal,
    tradingPlatformModal,
    regularCampaignAccountTypeModal,
    leverageModal,
    handleSetAccountOrCampaign,
    handleCurrency,
    handleSetAccountType,
    handleSetLeverage,
    updatedAccountTypeName,
  }
}
